import * as R from "ramda";
import { createSelector } from "reselect";
import { getters } from "./model";
import { getters as PortalHomeGetters } from "Portal/NewPortal/PortalHome/model";
import { isEventFeatureEnabled } from "redux/modules/event/selectors";
import { FEATURES } from "utils/event-types-and-feature-constants";
import {
  LOCATION_TYPE_IDS,
  LOCATION_TYPES,
  TABS_ADVANCED_GROUP,
  TAB_IDS
} from "./constants";
// import { getIsManageSessionsEnabled } from "Portal/NewPortal/PortalHome/selectors";
import { isEmptyOrNil } from "utils/General";
import * as FormSelectors from "ui-kit/Form/selectors";
import * as FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";
import moment from "moment";

export const getEnableContentUpload = createSelector(
  getters.contentSettings,
  settings => R.propOr(false, "enable_content_upload", settings)
);

export const getEditedField = R.compose(
  field => R.prop("value", field),
  FormSelectors.getField
);

export const getFormHasChanges = createSelector(
  getters.updatedFieldValues,
  updatedFieldValues => {
    return Object.keys(updatedFieldValues).length > 0;
  }
);

export const getIsValid = createSelector(
  getters.fieldsErrorMessage,
  getters.updatedFieldValues,
  (fieldsErrorMessage, updatedFieldValues) => {
    let requiredFieldMessage = null;
    let requiredFieldIds = [FIELD_IDS.SESSIONS.TITLE];
    const updatedFieldIds = Object.keys(updatedFieldValues);

    for (const requiredFieldId of requiredFieldIds) {
      if (
        updatedFieldIds.includes(requiredFieldId) &&
        !updatedFieldValues[requiredFieldId]?.length
      ) {
        requiredFieldMessage = `Fill in the required fields`;
      }
    }

    return requiredFieldMessage
      ? requiredFieldMessage
      : fieldsErrorMessage
      ? false
      : true;
  }
);

export const getSessionTasks = createSelector(
  PortalHomeGetters.sessionsTasks,
  getters.sessionData,
  (sessionTasks, sessionData) => {
    const session = sessionTasks.find(s => s.id === sessionData.id);
    if (session && session.tasks) {
      return session.tasks;
    }
    return [];
  }
);

export const isExpoEnabled = createSelector(isEventFeatureEnabled, isEnabled =>
  isEnabled(FEATURES.EXPO)
);

export const isBiddingEnabled = createSelector(
  isEventFeatureEnabled,
  isEnabled => isEnabled(FEATURES.BIDDING)
);

export const getSessionTabs = createSelector(
  getters.sessionData,
  getEnableContentUpload,
  getSessionTasks,
  (sessionData, enableContentUpload, sessionTasks) => {
    const isSpeaker = sessionData && sessionData.isSpeaker ? true : false;
    const isSubsession = sessionData && sessionData.isSubsession ? true : false;
    const hasSubsessions =
      sessionData && sessionData.subsessions && sessionData.subsessions.length
        ? true
        : false;

    // If content upload is disabled or if the session is a subsession and the speaker is not the session speaker, hide the file content tab
    // Otherwise, show the file content tab
    let tabs =
      !enableContentUpload || (isSubsession && !isSpeaker)
        ? R.filter(tab => tab.id !== TAB_IDS.FILE_CONTENT, TABS_ADVANCED_GROUP)
        : TABS_ADVANCED_GROUP;

    // If there are no subsessions, hide the subsessions tab
    if (!hasSubsessions) {
      tabs = tabs.filter(tab => tab.id !== TAB_IDS.SUBSESSIONS);
    }

    // If there are no tasks, hide the tasks tab
    if (isEmptyOrNil(sessionTasks)) {
      tabs = tabs.filter(tab => tab.id !== TAB_IDS.TASKS);
    }

    return tabs;
  }
);

export const getSessionStartsAtField = createSelector(
  getters.sessionData,
  sessionData => {
    let fields = sessionData.fields || [];
    return fields.find(f => f.id === FIELD_IDS.SESSIONS.STARTS_AT);
  }
);

export const getSessionEndsAtField = createSelector(
  getters.sessionData,
  sessionData => {
    let fields = sessionData.fields || [];
    return fields.find(f => f.id === FIELD_IDS.SESSIONS.ENDS_AT);
  }
);

export const getEditableSessionFields = createSelector(
  getters.sessionData,
  sessionData => {
    let fields = sessionData.fields || [];
    fields = fields.filter(f => f.is_editable);

    // pair event fields by every 2 fields
    const pairedFields = [];
    let i = 0;
    const SINGLE_ROW_FIELD_TYPES = ["textarea", "wysiwyg"];
    const SINGLE_ROW_FIELD_IDS = [FIELD_IDS.SESSIONS.TITLE];

    while (i < fields.length) {
      if (
        SINGLE_ROW_FIELD_IDS.includes(fields[i]?.id) ||
        SINGLE_ROW_FIELD_TYPES.includes(fields[i]?.type) ||
        SINGLE_ROW_FIELD_TYPES.includes(fields[i + 1]?.type)
      ) {
        pairedFields.push([fields[i]].filter(f => f));
        i += 1;
      } else {
        pairedFields.push([fields[i], fields[i + 1]].filter(f => f));
        i += 2;
      }
    }

    return pairedFields;
  }
);

export const getLocationOptions = createSelector(
  isExpoEnabled,
  isBiddingEnabled,
  (isExpoEnabled, isBiddingEnabled) => {
    let typesToReturn = LOCATION_TYPES;
    if (!isExpoEnabled) {
      typesToReturn = typesToReturn.filter(
        t => t.id !== LOCATION_TYPE_IDS.BOOTH
      );
    }
    if (!isBiddingEnabled) {
      typesToReturn = typesToReturn.filter(
        t => t.id !== LOCATION_TYPE_IDS.BIDDING
      );
    }
    return typesToReturn;
  }
);

export const getSubsessions = createSelector(
  getters.sessionData,
  sessionData => {
    return sessionData && sessionData.subsessions
      ? R.compose(
          R.map(s => ({
            title: s.title,
            speakers: s.speakers.map(s => ({
              id: s.id,
              name: [s.first_name, s.last_name]
                .filter(v => v && v.length)
                .join(" ")
            })),
            startsAt: s.starts_at
              ? moment(s.starts_at).format("HH:mm A")
              : null,
            endsAt: s.ends_at ? moment(s.ends_at).format("HH:mm A") : null
          })),
          R.sortBy(s => s.starts_at),
          R.sortBy(s => s.title)
        )(sessionData.subsessions)
      : [];
  }
);
